import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useRoute } from '../../../contexts/RouteContext';
import {
  formatDistance,
  formatDuration,
  getGoogleMapsUrl,
  getCitymapperUrl,
  getWazeUrl,
  getTransportModeIcon,
  getTransportModeLabel,
  TransportMode,
  RoutePoint,
} from '../../../services/routing';
import './RoutePanel.css';

interface RoutePanelProps {
  t: (key: string) => string;
  language: 'fr' | 'en';
  onSearchPlace?: (query: string) => Promise<any[]>;
  friends?: Array<{
    id: number;
    first_name: string;
    last_name: string;
    latitude?: number;
    longitude?: number;
    city?: string;
    profile_photo?: string;
  }>;
  onClose?: () => void;
}

const TRANSPORT_MODES: TransportMode[] = ['driving', 'cycling', 'walking', 'wheelchair'];

export const RoutePanel: React.FC<RoutePanelProps> = ({
  t,
  language,
  onSearchPlace,
  friends = [],
  onClose,
}) => {
  const {
    isRoutingMode,
    origin,
    setOrigin,
    destination,
    setDestination,
    waypoints,
    addWaypoint,
    removeWaypoint,
    routingResult,
    selectedRouteIndex,
    setSelectedRouteIndex,
    selectedRoute,
    transportMode,
    setTransportMode,
    isCalculating,
    error,
    swapOriginDestination,
    exitRoutingMode,
    reorderWaypoints,
  } = useRoute();

  // Mobile bottom sheet state
  const [sheetHeight, setSheetHeight] = useState<'collapsed' | 'half' | 'full'>('half');
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(0);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Search states for origin/destination/waypoint inputs
  const [originSearch, setOriginSearch] = useState('');
  const [destinationSearch, setDestinationSearch] = useState('');
  const [waypointSearch, setWaypointSearch] = useState('');
  const [originSuggestions, setOriginSuggestions] = useState<any[]>([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState<any[]>([]);
  const [waypointSuggestions, setWaypointSuggestions] = useState<any[]>([]);
  const [activeField, setActiveField] = useState<'origin' | 'destination' | 'waypoint' | null>(null);
  const [showWaypointInput, setShowWaypointInput] = useState(false);

  // Drag & drop state for waypoint reordering
  const [draggedWaypointIndex, setDraggedWaypointIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);


  const locale = language === 'fr' ? 'fr' : 'en';

  // Update search fields when origin/destination change from context
  useEffect(() => {
    if (origin?.name || origin?.address) {
      setOriginSearch(origin.name || origin.address || '');
    } else {
      setOriginSearch('');
    }
  }, [origin]);

  useEffect(() => {
    if (destination?.name || destination?.address) {
      setDestinationSearch(destination.name || destination.address || '');
    } else {
      setDestinationSearch('');
    }
  }, [destination]);

  // Search handler with debounce
  const handleSearch = useCallback(async (query: string, field: 'origin' | 'destination' | 'waypoint') => {
    if (!query.trim() || query.length < 2) {
      if (field === 'origin') setOriginSuggestions([]);
      else if (field === 'destination') setDestinationSuggestions([]);
      else setWaypointSuggestions([]);
      return;
    }

    // Search friends first
    const friendMatches = friends.filter(f =>
      `${f.first_name} ${f.last_name}`.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 3);

    // Then search places
    let placeMatches: any[] = [];
    if (onSearchPlace) {
      placeMatches = await onSearchPlace(query);
    }

    const suggestions = [
      ...friendMatches.map(f => ({
        type: 'friend' as const,
        id: f.id,
        name: `${f.first_name} ${f.last_name}`,
        address: f.city || '',
        lat: f.latitude,
        lng: f.longitude,
        // Include friend info for route markers
        firstName: f.first_name,
        lastName: f.last_name,
        profilePhoto: (f as any).profile_photo,
      })),
      ...placeMatches.slice(0, 5).map(p => ({
        type: 'place' as const,
        name: p.display_name?.split(',')[0] || p.name,
        address: p.display_name,
        lat: parseFloat(p.lat),
        lng: parseFloat(p.lon),
      })),
    ];

    if (field === 'origin') setOriginSuggestions(suggestions);
    else if (field === 'destination') setDestinationSuggestions(suggestions);
    else setWaypointSuggestions(suggestions);
  }, [friends, onSearchPlace]);

  // Debounced search
  useEffect(() => {
    if (activeField === 'origin') {
      const timer = setTimeout(() => handleSearch(originSearch, 'origin'), 300);
      return () => clearTimeout(timer);
    }
  }, [originSearch, activeField, handleSearch]);

  useEffect(() => {
    if (activeField === 'destination') {
      const timer = setTimeout(() => handleSearch(destinationSearch, 'destination'), 300);
      return () => clearTimeout(timer);
    }
  }, [destinationSearch, activeField, handleSearch]);

  useEffect(() => {
    if (activeField === 'waypoint') {
      const timer = setTimeout(() => handleSearch(waypointSearch, 'waypoint'), 300);
      return () => clearTimeout(timer);
    }
  }, [waypointSearch, activeField, handleSearch]);

  // Select suggestion
  const handleSelectSuggestion = (suggestion: any, field: 'origin' | 'destination' | 'waypoint') => {
    // Create RoutePoint with all marker info for proper rendering
    const point: RoutePoint = {
      lat: suggestion.lat,
      lng: suggestion.lng,
      name: suggestion.name,
      address: suggestion.address,
      // Include marker rendering info based on suggestion type
      markerType: suggestion.type === 'friend' ? 'friend' : 'place',
      ...(suggestion.type === 'friend' && {
        friendId: suggestion.id,
        firstName: suggestion.firstName,
        lastName: suggestion.lastName,
        profilePhoto: suggestion.profilePhoto,
      }),
    };

    if (field === 'origin') {
      setOrigin(point);
      setOriginSearch(suggestion.name);
      setOriginSuggestions([]);
    } else if (field === 'destination') {
      setDestination(point);
      setDestinationSearch(suggestion.name);
      setDestinationSuggestions([]);
    } else {
      // Add as waypoint
      addWaypoint(point);
      setWaypointSearch('');
      setWaypointSuggestions([]);
      setShowWaypointInput(false);
    }
    setActiveField(null);
  };

  // Mobile touch handlers for bottom sheet
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setStartHeight(sheetRef.current?.offsetHeight || 0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const currentY = e.touches[0].clientY;
    const diff = startY - currentY;
    const windowHeight = window.innerHeight;

    // Calculate new height percentage
    const newHeightPx = startHeight + diff;
    const newHeightPercent = (newHeightPx / windowHeight) * 100;

    // Snap to positions
    if (newHeightPercent < 20) {
      setSheetHeight('collapsed');
    } else if (newHeightPercent < 60) {
      setSheetHeight('half');
    } else {
      setSheetHeight('full');
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Handle close/exit
  const handleClose = () => {
    exitRoutingMode();
    onClose?.();
  };

  if (!isRoutingMode) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Height classes for mobile bottom sheet
  const heightClass = isMobile ? `sheet-${sheetHeight}` : '';

  return (
    <div
      ref={sheetRef}
      className={`route-panel ${isMobile ? 'mobile' : 'desktop'} ${heightClass}`}
    >
      {/* Mobile drag handle */}
      {isMobile && (
        <div
          className="route-panel-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="handle-bar" />
        </div>
      )}

      {/* Fixed header zone - doesn't scroll */}
      <div className="route-panel-fixed-header">
        {/* Header with title, transport modes, and close */}
        <div className="route-panel-header">
          <h3>{t('routing.title')}</h3>
          <div className="transport-mode-selector">
            {TRANSPORT_MODES.map(mode => (
              <button
                key={mode}
                className={`transport-mode-btn ${transportMode === mode ? 'active' : ''}`}
                onClick={() => setTransportMode(mode)}
                title={getTransportModeLabel(mode, locale)}
              >
                {getTransportModeIcon(mode)}
              </button>
            ))}
          </div>
          <button className="route-panel-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        {/* Route inputs - 2 lines */}
        <div className="route-inputs-fixed">
          {/* Origin row */}
          <div
            className={`route-input-row ${dragOverIndex === -1 ? 'drag-over' : ''}`}
            onDragOver={(e) => {
              if (waypoints.length > 0 && draggedWaypointIndex !== null) {
                e.preventDefault();
                setDragOverIndex(-1); // -1 = origin
              }
            }}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedWaypointIndex !== null && origin) {
                // Swap dragged waypoint with origin
                const draggedWp = waypoints[draggedWaypointIndex];
                setOrigin(draggedWp);
                removeWaypoint(draggedWaypointIndex);
                addWaypoint(origin, 0);
              }
              setDraggedWaypointIndex(null);
              setDragOverIndex(null);
            }}
          >
            {/* Drag handle - only visible when there are waypoints */}
            {waypoints.length > 0 && (
              <span className="drag-handle drag-handle-disabled" title="Déposer une étape ici">⋮⋮</span>
            )}
            <span className="route-marker origin"></span>
            <div className="route-input-wrapper">
              <input
                type="text"
                className="route-input"
                placeholder={t('routing.origin')}
                value={originSearch}
                onChange={(e) => setOriginSearch(e.target.value)}
                onFocus={() => setActiveField('origin')}
                onBlur={() => setTimeout(() => setActiveField(null), 200)}
              />
              {originSuggestions.length > 0 && activeField === 'origin' && (
                <div className="route-suggestions">
                  {originSuggestions.map((s, i) => (
                    <div
                      key={i}
                      className="route-suggestion-item"
                      onClick={() => handleSelectSuggestion(s, 'origin')}
                    >
                      <span className="suggestion-icon">
                        {s.type === 'friend' ? '👤' : '📍'}
                      </span>
                      <div className="suggestion-text">
                        <span className="suggestion-name">{s.name}</span>
                        {s.address && (
                          <span className="suggestion-address">{s.address}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Swap button - only visible when NO waypoints */}
            {waypoints.length === 0 && (
              <button
                className="swap-points-btn"
                onClick={swapOriginDestination}
                title={t('routing.swap')}
              >
                ⇅
              </button>
            )}
          </div>

          {/* Waypoints */}
          {waypoints.map((wp, index) => (
            <div
              key={index}
              className={`route-input-row waypoint-draggable ${draggedWaypointIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
              draggable
              onDragStart={(e) => {
                setDraggedWaypointIndex(index);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragEnd={() => {
                setDraggedWaypointIndex(null);
                setDragOverIndex(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (draggedWaypointIndex !== null && draggedWaypointIndex !== index) {
                  setDragOverIndex(index);
                }
              }}
              onDragLeave={() => {
                setDragOverIndex(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedWaypointIndex !== null && draggedWaypointIndex !== index) {
                  reorderWaypoints(draggedWaypointIndex, index);
                }
                setDraggedWaypointIndex(null);
                setDragOverIndex(null);
              }}
            >
              <span className="drag-handle" title="Glisser pour réorganiser">⋮⋮</span>
              <span className="route-marker waypoint"></span>
              <div className="route-input-wrapper">
                <input
                  type="text"
                  className="route-input"
                  value={wp.name || wp.address || ''}
                  readOnly
                />
              </div>
              <div className="route-row-actions">
                <button
                  className="remove-waypoint-btn"
                  onClick={() => removeWaypoint(index)}
                  title={t('routing.removeWaypoint')}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}

          {/* Add waypoint input (shown when clicking +) */}
          {showWaypointInput && (
            <div className="route-input-row">
              <span className="route-marker waypoint"></span>
              <div className="route-input-wrapper">
                <input
                  type="text"
                  className="route-input"
                  placeholder={t('routing.addWaypoint')}
                  value={waypointSearch}
                  onChange={(e) => setWaypointSearch(e.target.value)}
                  onFocus={() => setActiveField('waypoint')}
                  onBlur={() => setTimeout(() => {
                    setActiveField(null);
                    // Hide input if empty after blur
                    if (!waypointSearch.trim()) {
                      setShowWaypointInput(false);
                    }
                  }, 200)}
                  autoFocus
                />
                {waypointSuggestions.length > 0 && activeField === 'waypoint' && (
                  <div className="route-suggestions">
                    {waypointSuggestions.map((s, i) => (
                      <div
                        key={i}
                        className="route-suggestion-item"
                        onClick={() => handleSelectSuggestion(s, 'waypoint')}
                      >
                        <span className="suggestion-icon">
                          {s.type === 'friend' ? '👤' : '📍'}
                        </span>
                        <div className="suggestion-text">
                          <span className="suggestion-name">{s.name}</span>
                          {s.address && (
                            <span className="suggestion-address">{s.address}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="route-row-actions">
                <button
                  className="remove-waypoint-btn"
                  onClick={() => {
                    setShowWaypointInput(false);
                    setWaypointSearch('');
                    setWaypointSuggestions([]);
                  }}
                  title={t('common.cancel')}
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Destination row */}
          <div
            className={`route-input-row ${dragOverIndex === -2 ? 'drag-over' : ''}`}
            onDragOver={(e) => {
              if (waypoints.length > 0 && draggedWaypointIndex !== null) {
                e.preventDefault();
                setDragOverIndex(-2); // -2 = destination
              }
            }}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedWaypointIndex !== null && destination) {
                // Swap dragged waypoint with destination
                const draggedWp = waypoints[draggedWaypointIndex];
                setDestination(draggedWp);
                removeWaypoint(draggedWaypointIndex);
                addWaypoint(destination); // Add at end
              }
              setDraggedWaypointIndex(null);
              setDragOverIndex(null);
            }}
          >
            {/* Drag handle - only visible when there are waypoints */}
            {waypoints.length > 0 && (
              <span className="drag-handle drag-handle-disabled" title="Déposer une étape ici">⋮⋮</span>
            )}
            <span className="route-marker destination"></span>
            <div className="route-input-wrapper">
              <input
                type="text"
                className="route-input"
                placeholder={t('routing.destination')}
                value={destinationSearch}
                onChange={(e) => setDestinationSearch(e.target.value)}
                onFocus={() => setActiveField('destination')}
                onBlur={() => setTimeout(() => setActiveField(null), 200)}
              />
              {destinationSuggestions.length > 0 && activeField === 'destination' && (
                <div className="route-suggestions">
                  {destinationSuggestions.map((s, i) => (
                    <div
                      key={i}
                      className="route-suggestion-item"
                      onClick={() => handleSelectSuggestion(s, 'destination')}
                    >
                      <span className="suggestion-icon">
                        {s.type === 'friend' ? '👤' : '📍'}
                      </span>
                      <div className="suggestion-text">
                        <span className="suggestion-name">{s.name}</span>
                        {s.address && (
                          <span className="suggestion-address">{s.address}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="route-row-actions">
              <button
                className={`add-waypoint-btn ${showWaypointInput ? 'active' : ''}`}
                onClick={() => {
                  setShowWaypointInput(!showWaypointInput);
                  if (!showWaypointInput) {
                    setWaypointSearch('');
                    setWaypointSuggestions([]);
                  }
                }}
                title={t('routing.addWaypoint')}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="route-panel-content">
        {/* Loading indicator */}
      {isCalculating && (
        <div className="route-loading">
          <div className="route-spinner" />
          <span>{t('routing.calculating')}</span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="route-error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Route results */}
      {routingResult && routingResult.routes.length > 0 && (
        <div className="route-results">
          {/* Route alternatives */}
          {routingResult.routes.length > 1 && (
            <div className="route-alternatives">
              <span className="alternatives-label">{t('routing.alternatives')}:</span>
              <div className="alternatives-list">
                {routingResult.routes.map((route, index) => (
                  <button
                    key={route.id}
                    className={`alternative-btn ${selectedRouteIndex === index ? 'active' : ''}`}
                    onClick={() => setSelectedRouteIndex(index)}
                  >
                    <span className="alt-duration">{formatDuration(route.duration, locale)}</span>
                    <span className="alt-distance">{formatDistance(route.distance, locale)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selected route info */}
          {selectedRoute && (
            <div className="route-info">
              <div className="route-summary">
                <div className="route-stat">
                  <span className="stat-icon">⏱️</span>
                  <span className="stat-value">{formatDuration(selectedRoute.duration, locale)}</span>
                </div>
                <div className="route-stat">
                  <span className="stat-icon">📏</span>
                  <span className="stat-value">{formatDistance(selectedRoute.distance, locale)}</span>
                </div>
                <div className="route-stat provider">
                  <span className="stat-label">via {routingResult.provider.toUpperCase()}</span>
                </div>
              </div>

              {/* Turn-by-turn instructions (collapsible) */}
              {selectedRoute.steps && selectedRoute.steps.length > 0 && (
                <details className="route-steps">
                  <summary>{t('routing.instructions')}</summary>
                  <ol className="steps-list">
                    {selectedRoute.steps.map((step, index) => (
                      <li key={index} className="step-item">
                        <span className="step-instruction">{step.instruction || step.name}</span>
                        <span className="step-distance">{formatDistance(step.distance, locale)}</span>
                      </li>
                    ))}
                  </ol>
                </details>
              )}
            </div>
          )}

          {/* Open in external apps */}
          {origin && destination && (
            <div className="route-external-apps">
              <span className="external-apps-label">{t('routing.openIn')}</span>
              <div className="external-apps-buttons">
                <a
                  href={getGoogleMapsUrl(origin, destination, waypoints, transportMode)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external-app-btn"
                  title="Google Maps"
                >
                  <img
                    src="https://www.google.com/images/branding/product/1x/maps_64dp.png"
                    alt="Google Maps"
                    className="external-app-logo"
                  />
                </a>
                <a
                  href={getWazeUrl(destination)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external-app-btn"
                  title="Waze"
                >
                  <img
                    src="https://www.waze.com/favicon.ico"
                    alt="Waze"
                    className="external-app-logo"
                  />
                </a>
                <a
                  href={getCitymapperUrl(origin, destination)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external-app-btn"
                  title="Citymapper"
                >
                  <img
                    src="https://citymapper.com/favicon.ico"
                    alt="Citymapper"
                    className="external-app-logo"
                  />
                </a>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default RoutePanel;
